<script lang="ts" generics="TValues">
    import Row from '$components/Form/Row.svelte';
    import type { Form } from '@jmvdevelop/svelte-form';
    import type { Fields } from './AssuranceFormData';
    import Select from '$components/Form/Select.svelte';
    import PrettyChoice from '$components/Form/PrettyChoice.svelte';
    import { quadOut } from 'svelte/easing';
    import { fly } from 'svelte/transition';
    import Input from '$components/Form/Input.svelte';
    import range from 'lodash/range';
    import DatePickerInput, {
        dateNullable,
    } from '$components/Date/DatePickerInput.svelte';

    export let form: Form<TValues, string>;
    export let fields: Fields<TValues>;

    const assuranceTypeOptions = (
        [
            ['auto', `Assurance auto`],
            ['home', `Assurance habitation`],
            ['health', `Assurance santé`],
            ['life', `Assurance vie`],
            ['borrower', `Assurance emprunteur (prêt immobilier)`],
            ['pro', `Assurance professionnelle`],
            ['travel', `Assurance voyage`],
            ['temporary', `Assurance temporaire`],
            ['pet', `Assurance animaux (chien, chat)`],
            ['other', `Autre`],
        ] as const
    ).map(([v, label]) => ({ label, value: v, key: v }));

    $: assuranceType = form.stores().fieldValue(fields.assuranceType);

    const currentYear = new Date().getFullYear();
</script>

<div class="flex flex-col gap-8 @container">
    <Row
        {form}
        field={fields.isIndividualOrProfessional}
        class=""
        let:field
        let:id
        label={`Êtes-vous un particulier ou un professionnel ?`}
    >
        <PrettyChoice
            {form}
            {field}
            {id}
            defaultClass="grid grid-cols-1 @[280px]:grid-cols-2 gap-x-6 gap-y-0"
            choices={['Particulier', 'Professionnel / Entreprise'].map((v) => ({
                label: v,
                value: v,
                key: v,
            }))}
        />
    </Row>

    <Row
        {form}
        field={fields.assuranceType}
        class=""
        let:field
        let:id
        label={`Type d’assurance recherchée`}
    >
        <Select
            {form}
            {id}
            {field}
            required={false}
            showChevron
            clearable
            options={assuranceTypeOptions}
        />
    </Row>

    {#if $assuranceType === 'other'}
        <div
            transition:fly={{ duration: 150, easing: quadOut, y: '-10px', opacity: 0 }}
            class="flex flex-col gap-8"
        >
            <Row
                {form}
                field={fields.assuranceTypeOther}
                class=""
                let:field
                let:id
                label={`Précisez le type d'assurance`}
            >
                <Input {form} {field} {id} toText={(v) => v} fromText={(v) => v} />
            </Row>
        </div>
    {:else if $assuranceType === 'auto'}
        <div
            transition:fly={{ duration: 150, easing: quadOut, y: '-10px', opacity: 0 }}
            class="grid-auto"
        >
            <div class="[grid-area:vehicleType]">
                <Row
                    {form}
                    field={fields.vehicleType}
                    class=""
                    let:field
                    let:id
                    label={`Type de véhicule`}
                >
                    <Select
                        {form}
                        {id}
                        {field}
                        required={false}
                        showChevron
                        clearable
                        options={['Voiture', 'Moto', 'Utilitaire', 'Autre'].map((v) => ({
                            label: v,
                            value: v,
                            key: v,
                        }))}
                    />
                </Row>
            </div>

            <div class="[grid-area:vehicleBrandAndModel]">
                <Row
                    {form}
                    field={fields.vehicleBrandAndModel}
                    class=""
                    let:field
                    let:id
                    label={`Marque et modèle`}
                >
                    <Input {form} {field} {id} toText={(v) => v} fromText={(v) => v} />
                </Row>
            </div>

            <div class="[grid-area:vehicleYear]">
                <Row
                    {form}
                    field={fields.vehicleYear}
                    class=""
                    let:field
                    let:id
                    label={`Année de mise en circulation`}
                >
                    <Select
                        {form}
                        {id}
                        {field}
                        required={false}
                        showChevron
                        clearable
                        options={range(2000, currentYear + 1).map((v) => ({
                            label: `${v}`,
                            value: v,
                            key: `${v}`,
                        }))}
                    />
                </Row>
            </div>

            <div class="[grid-area:vehicleUsage]">
                <Row
                    {form}
                    field={fields.vehicleUsage}
                    class=""
                    let:field
                    let:id
                    label={`Usage du véhicule`}
                >
                    <PrettyChoice
                        {form}
                        {field}
                        {id}
                        defaultClass="grid grid-cols-1 @[260px]:grid-cols-3 gap-x-6 gap-y-0"
                        choices={['Privé', 'Pro', 'Mixte'].map((v) => ({
                            label: v,
                            value: v,
                            key: v,
                        }))}
                    />
                </Row>
            </div>

            <div class="[grid-area:desiredCoverage]">
                <Row
                    {form}
                    field={fields.desiredCoverage}
                    class=""
                    let:field
                    let:id
                    label={`Garantie souhaitée`}
                >
                    <PrettyChoice
                        {form}
                        {field}
                        {id}
                        defaultClass="grid grid-cols-1 @[310px]:grid-cols-3 gap-x-6 gap-y-0"
                        choices={['Tiers', 'Tiers +', 'Tous risques'].map((v) => ({
                            label: v,
                            value: v,
                            key: v,
                        }))}
                    />
                </Row>
            </div>
        </div>
    {:else if $assuranceType === 'home'}
        <div
            transition:fly={{ duration: 150, easing: quadOut, y: '-10px', opacity: 0 }}
            class="grid-home"
        >
            <div class="[grid-area:housingType]">
                <Row
                    {form}
                    field={fields.housingType}
                    class=""
                    let:field
                    let:id
                    label={`Type de logement`}
                >
                    <PrettyChoice
                        {form}
                        {field}
                        {id}
                        defaultClass="grid grid-cols-1 @[280px]:grid-cols-2 gap-x-6 gap-y-0"
                        choices={['Appartement', 'Maison'].map((v) => ({
                            label: v,
                            value: v,
                            key: v,
                        }))}
                    />
                </Row>
            </div>

            <div class="[grid-area:isOwnerOrTenant]">
                <Row
                    {form}
                    field={fields.isOwnerOrTenant}
                    class=""
                    let:field
                    let:id
                    label={`Propriétaire ou locataire ?`}
                >
                    <PrettyChoice
                        {form}
                        {field}
                        {id}
                        defaultClass="grid grid-cols-1 @[260px]:grid-cols-2 gap-x-6 gap-y-0"
                        choices={['Propriétaire', 'Locataire'].map((v) => ({
                            label: v,
                            value: v,
                            key: v,
                        }))}
                    />
                </Row>
            </div>

            <div class="[grid-area:housingSurface]">
                <Row
                    {form}
                    field={fields.housingSurface}
                    class=""
                    let:field
                    let:id
                    label={`Surface du logement (en m²)`}
                >
                    <div class="flex gap-2">
                        <Input
                            {form}
                            {field}
                            {id}
                            class="flex-auto"
                            toText={(v) => (v == null ? null : `${v}`)}
                            fromText={(v) => (v == null ? null : Number(v))}
                            placeholder="Surface du logement"
                            type="number"
                        />
                        <div
                            class="control-form bg-gray-200 text-gray-700 readonly basis-[content] italic"
                        >
                            m²
                        </div>
                    </div>
                </Row>
            </div>

            <div class="[grid-area:insuredGoodsValue]">
                <Row
                    {form}
                    field={fields.insuredGoodsValue}
                    class=""
                    let:field
                    let:id
                    label={`Valeur du bien à assurer`}
                >
                    <Input
                        {form}
                        {field}
                        {id}
                        class="flex-auto"
                        toText={(v) => v}
                        fromText={(v) => v}
                        type="string"
                    />
                </Row>
            </div>
        </div>
    {:else if $assuranceType === 'health'}
        <div
            transition:fly={{ duration: 150, easing: quadOut, y: '-10px', opacity: 0 }}
            class="grid-health"
        >
            <div class="[grid-area:subscriberAge]">
                <Row
                    {form}
                    field={fields.subscriberAge}
                    class=""
                    let:field
                    let:id
                    label={`Âge du souscripteur`}
                >
                    <Input
                        {form}
                        {field}
                        {id}
                        toText={(v) => (v == null ? null : `${v}`)}
                        fromText={(v) => (v == null ? null : Number(v))}
                        type="number"
                    />
                </Row>
            </div>
            <div class="[grid-area:numberOfPeopleCovered]">
                <Row
                    {form}
                    field={fields.numberOfPeopleCovered}
                    class=""
                    let:field
                    let:id
                    label={`Nombre de personnes à couvrir`}
                >
                    <Select
                        {form}
                        {id}
                        {field}
                        required={false}
                        showChevron
                        clearable
                        options={['1', '2', 'Famille'].map((v) => ({
                            label: v,
                            value: v,
                            key: v,
                        }))}
                    />
                </Row>
            </div>
            <div class="[grid-area:desiredReimbursementLevel]">
                <Row
                    {form}
                    field={fields.desiredReimbursementLevel}
                    class=""
                    let:field
                    let:id
                    label={`Niveau de remboursement souhaité`}
                >
                    <Select
                        {form}
                        {id}
                        {field}
                        required={false}
                        showChevron
                        clearable
                        options={['Base', 'Intermédiaire', 'Premium'].map((v) => ({
                            label: v,
                            value: v,
                            key: v,
                        }))}
                    />
                </Row>
            </div>
        </div>
    {:else if $assuranceType === 'pro'}
        <div
            transition:fly={{ duration: 150, easing: quadOut, y: '-10px', opacity: 0 }}
            class="flex flex-col gap-8"
        >
            <Row
                {form}
                field={fields.activitySector}
                class=""
                let:field
                let:id
                label={`Secteur d'activité`}
            >
                <Input {form} {field} {id} toText={(v) => v} fromText={(v) => v} />
            </Row>
            <Row
                {form}
                field={fields.employeeCount}
                class=""
                let:field
                let:id
                label={`Nombre d'employés`}
            >
                <PrettyChoice
                    {form}
                    {field}
                    {id}
                    defaultClass="grid grid-cols-1 @[150px]:grid-cols-2 @[308px]:grid-cols-4 gap-x-6 gap-y-0"
                    choices={['1-5', '6-10', '11-50', '50+'].map((v) => ({
                        label: v,
                        value: v,
                        key: v,
                    }))}
                />
            </Row>
            <Row
                {form}
                field={fields.coverageType}
                class=""
                let:field
                let:id
                label={`Type de couverture souhaitée`}
            >
                <PrettyChoice
                    {form}
                    {field}
                    {id}
                    defaultClass="grid grid-cols-1 @[240px]:grid-cols-2 gap-x-6 gap-y-0"
                    choices={[
                        'RC Pro',
                        'Multirisque',
                        'Décennale',
                        'Flotte auto',
                        'Autre',
                    ].map((v) => ({ label: v, value: v, key: v }))}
                />
            </Row>
        </div>
    {:else if $assuranceType === 'travel'}
        <div
            transition:fly={{ duration: 150, easing: quadOut, y: '-10px', opacity: 0 }}
            class="grid-travel"
        >
            <div class="[grid-area:travelDestination]">
                <Row
                    {form}
                    field={fields.travelDestination}
                    class=""
                    let:field
                    let:id
                    label={`Destination du voyage`}
                >
                    <Input {form} {field} {id} toText={(v) => v} fromText={(v) => v} />
                </Row>
            </div>
            <div class="[grid-area:travelDuration]">
                <Row
                    {form}
                    field={fields.travelDuration}
                    class=""
                    let:field
                    let:id
                    label={`Durée du séjour`}
                >
                    <Select
                        {form}
                        {id}
                        {field}
                        required={false}
                        showChevron
                        clearable
                        options={[
                            'Week-end',
                            '1 semaine',
                            '2 semaines',
                            '1 mois',
                            `Plus d'un mois`,
                        ].map((v) => ({ label: v, value: v, key: v }))}
                    />
                </Row>
            </div>
            <div class="[grid-area:travelNumberOfPeople]">
                <Row
                    {form}
                    field={fields.travelNumberOfPeople}
                    class=""
                    let:field
                    let:id
                    label={`Nombre de personne à couvrir`}
                >
                    <Input
                        {form}
                        {field}
                        {id}
                        toText={(v) => (v == null ? null : `${v}`)}
                        fromText={(v) => (v == null ? null : Number(v))}
                        type="number"
                    />
                </Row>
            </div>
        </div>
    {:else if $assuranceType === 'pet'}
        <div
            transition:fly={{ duration: 150, easing: quadOut, y: '-10px', opacity: 0 }}
            class="grid-pet"
        >
            <div class="[grid-area:animalType]">
                <Row
                    {form}
                    field={fields.animalType}
                    class=""
                    let:field
                    let:id
                    label={`Type d'animal`}
                >
                    <PrettyChoice
                        {form}
                        {field}
                        {id}
                        defaultClass="grid grid-cols-2 @[270px]:grid-cols-3 gap-x-6 gap-y-0"
                        choices={['Chien', 'Chat', 'Autre'].map((v) => ({
                            label: v,
                            value: v,
                            key: v,
                        }))}
                    />
                </Row>
            </div>
            <div class="[grid-area:animalBreed]">
                <Row
                    {form}
                    field={fields.animalBreed}
                    class=""
                    let:field
                    let:id
                    label={`Race de l'animal`}
                >
                    <Input {form} {field} {id} toText={(v) => v} fromText={(v) => v} />
                </Row>
            </div>
            <div class="[grid-area:animalAge]">
                <Row
                    {form}
                    field={fields.animalAge}
                    class=""
                    let:field
                    let:id
                    label={`Age de l'animal`}
                >
                    <Input {form} {field} {id} toText={(v) => v} fromText={(v) => v} />
                </Row>
            </div>
        </div>
    {:else if $assuranceType === 'life'}
        <div
            transition:fly={{ duration: 150, easing: quadOut, y: '-10px', opacity: 0 }}
            class="grid-life"
        >
            <div class="[grid-area:lifeMainObjective]">
                <Row
                    {form}
                    field={fields.lifeMainObjective}
                    class=""
                    let:field
                    let:id
                    label={`Quel est votre objectif principal ?`}
                >
                    <Select
                        {form}
                        {id}
                        {field}
                        required={false}
                        showChevron
                        clearable
                        options={[
                            'Prévoir pour vos proches',
                            'Investir',
                            'Optimiser votre succession',
                            'Autre',
                        ].map((v) => ({
                            label: v,
                            value: v,
                            key: v,
                        }))}
                    />
                </Row>
            </div>
            <div class="[grid-area:lifeSubscriberAge]">
                <Row
                    {form}
                    field={fields.lifeSubscriberAge}
                    class=""
                    let:field
                    let:id
                    label={`Âge du souscripteur`}
                >
                    <Input
                        {form}
                        {field}
                        {id}
                        toText={(v) => (v == null ? null : `${v}`)}
                        fromText={(v) => (v == null ? null : Number(v))}
                        type="number"
                    />
                </Row>
            </div>
            <div class="[grid-area:lifeEstimatedAmount]">
                <Row
                    {form}
                    field={fields.lifeEstimatedAmount}
                    class=""
                    let:field
                    let:id
                    label={`Montant estimé à assurer`}
                >
                    <Input {form} {field} {id} toText={(v) => v} fromText={(v) => v} />
                </Row>
            </div>
            <div class="[grid-area:lifeDesignatedBeneficiary]">
                <Row
                    {form}
                    field={fields.lifeDesignatedBeneficiary}
                    class=""
                    let:field
                    let:id
                    label={`Bénéficiaire(s) désigné(s) ?`}
                >
                    <PrettyChoice
                        {form}
                        {field}
                        {id}
                        defaultClass="grid grid-cols-1 @[339px]:grid-cols-3 gap-x-6 gap-y-0"
                        choices={['Oui', 'Non', 'À définir plus tard'].map((v) => ({
                            label: v,
                            value: v,
                            key: v,
                        }))}
                    />
                </Row>
            </div>
        </div>
    {:else if $assuranceType === 'borrower'}
        <div
            transition:fly={{ duration: 150, easing: quadOut, y: '-10px', opacity: 0 }}
            class="grid-borrower"
        >
            <div class="[grid-area:borrowerLoanAmount]">
                <Row
                    {form}
                    field={fields.borrowerLoanAmount}
                    class=""
                    let:field
                    let:id
                    label="Montant du prêt"
                >
                    <Input {form} {field} {id} toText={(v) => v} fromText={(v) => v} />
                </Row>
            </div>
            <div class="[grid-area:borrowerLoanDuration]">
                <Row
                    {form}
                    field={fields.borrowerLoanDuration}
                    class=""
                    let:field
                    let:id
                    label="Durée du prêt"
                >
                    <Select
                        {form}
                        {id}
                        {field}
                        required={false}
                        showChevron
                        clearable
                        options={[
                            '5 ans',
                            '10 ans',
                            '15 ans',
                            '20 ans',
                            '25 ans',
                            '30 ans',
                        ].map((v) => ({
                            label: v,
                            value: v,
                            key: v,
                        }))}
                    />
                </Row>
            </div>
            <div class="[grid-area:borrowerPropertyTypeFinanced]">
                <Row
                    {form}
                    field={fields.borrowerPropertyTypeFinanced}
                    class=""
                    let:field
                    let:id
                    label="Type de bien financé"
                >
                    <Select
                        {form}
                        {id}
                        {field}
                        required={false}
                        showChevron
                        clearable
                        options={[
                            'Résidence principale',
                            'Résidence secondaire',
                            'Investissement locatif',
                        ].map((v) => ({
                            label: v,
                            value: v,
                            key: v,
                        }))}
                    />
                </Row>
            </div>
            <div class="[grid-area:borrowerHasKnownHealthIssues]">
                <Row
                    {form}
                    field={fields.borrowerHasKnownHealthIssues}
                    class=""
                    let:field
                    let:id
                    label="Avez-vous des problèmes de santé connus ?"
                >
                    <Select
                        {form}
                        {id}
                        {field}
                        required={false}
                        showChevron
                        clearable
                        options={['Oui', 'Non', 'Préfère ne pas répondre'].map((v) => ({
                            label: v,
                            value: v,
                            key: v,
                        }))}
                    />
                </Row>
            </div>
            <div class="[grid-area:borrowerWantsToDelegateInsurance]">
                <Row
                    {form}
                    field={fields.borrowerWantsToDelegateInsurance}
                    class=""
                    let:field
                    let:id
                    label="Souhaitez-vous déléguer votre assurance (hors banque) ?"
                >
                    <Select
                        {form}
                        {id}
                        {field}
                        required={false}
                        showChevron
                        clearable
                        options={['Oui', 'Non', 'Je ne sais pas'].map((v) => ({
                            label: v,
                            value: v,
                            key: v,
                        }))}
                    />
                </Row>
            </div>
        </div>
    {:else if $assuranceType === 'temporary'}
        <div
            transition:fly={{ duration: 150, easing: quadOut, y: '-10px', opacity: 0 }}
            class="grid-temporary"
        >
            <div class="[grid-area:temporaryInsuredItemOrSituation]">
                <Row
                    {form}
                    field={fields.temporaryInsuredItemOrSituation}
                    class=""
                    let:field
                    let:id
                    label="Type de bien ou situation à assurer temporairement"
                >
                    <Select
                        {form}
                        {id}
                        {field}
                        required={false}
                        showChevron
                        clearable
                        options={[
                            'Véhicule',
                            'Habitation',
                            'Matériel',
                            'Personne',
                            'Autre',
                        ].map((v) => ({
                            label: v,
                            value: v,
                            key: v,
                        }))}
                    />
                </Row>
            </div>
            <div class="[grid-area:temporaryDesiredDuration]">
                <Row
                    {form}
                    field={fields.temporaryDesiredDuration}
                    class=""
                    let:field
                    let:id
                    label="Durée souhaitée de l’assurance"
                >
                    <PrettyChoice
                        {form}
                        {field}
                        {id}
                        defaultClass="grid grid-cols-1 @[280px]:grid-cols-3 gap-x-6 gap-y-0"
                        choices={['1 jour', '1 semaine', '1 mois', '3 mois', 'Autre'].map(
                            (v) => ({ label: v, value: v, key: v }),
                        )}
                    />
                </Row>
            </div>
            <div class="[grid-area:temporarySubscriptionReason]">
                <Row
                    {form}
                    field={fields.temporarySubscriptionReason}
                    class=""
                    let:field
                    let:id
                    label="Motif de la souscription temporaire"
                >
                    <Select
                        {form}
                        {id}
                        {field}
                        required={false}
                        showChevron
                        clearable
                        options={[
                            'Voyage',
                            'Location',
                            'Transition de contrat',
                            'Test',
                            'Autre',
                        ].map((v) => ({
                            label: v,
                            value: v,
                            key: v,
                        }))}
                    />
                </Row>
            </div>
            <div class="[grid-area:temporaryCoverageStartDate]">
                <Row
                    {form}
                    field={fields.temporaryCoverageStartDate}
                    class=""
                    let:field
                    label="Date de début de la couverture"
                >
                    <DatePickerInput futureDateOnly {field} {form} {...dateNullable} />
                </Row>
            </div>
        </div>
    {/if}
</div>

<style lang="postcss">
    .grid-auto {
        @apply grid gap-x-6 gap-y-8;

        grid-template:
            'vehicleType vehicleBrandAndModel' auto
            'vehicleYear vehicleYear' auto
            'vehicleUsage vehicleUsage' auto
            'desiredCoverage desiredCoverage' auto
            / 1fr 1fr;
    }

    @container (max-width: 390px) {
        .grid-auto {
            grid-template:
                'vehicleType' auto
                'vehicleBrandAndModel' auto
                'vehicleYear' auto
                'vehicleUsage' auto
                'desiredCoverage' auto
                / 1fr;
        }
    }

    .grid-home {
        @apply grid gap-x-6 gap-y-8;

        grid-template:
            'housingType        housingType' auto
            'isOwnerOrTenant    isOwnerOrTenant' auto
            'housingSurface     insuredGoodsValue' auto
            / 1fr 1fr;
    }

    @container (max-width: 488px) {
        .grid-home {
            grid-template:
                'housingType' auto
                'isOwnerOrTenant' auto
                'housingSurface' auto
                'insuredGoodsValue' auto
                / 1fr;
        }
    }

    .grid-health {
        @apply grid gap-x-6 gap-y-8;

        grid-template:
            'subscriberAge             numberOfPeopleCovered' auto
            'desiredReimbursementLevel desiredReimbursementLevel' auto
            / 1fr 1fr;
    }

    @container (max-width: 457px) {
        .grid-health {
            grid-template:
                'subscriberAge' auto
                'numberOfPeopleCovered' auto
                'desiredReimbursementLevel' auto
                / 1fr;
        }
    }

    .grid-travel {
        @apply grid gap-x-6 gap-y-8;

        grid-template:
            'travelDestination  travelDestination' auto
            'travelDuration     travelNumberOfPeople' auto
            / 1fr 1fr;
    }

    @container (max-width: 457px) {
        .grid-travel {
            grid-template:
                'travelDestination' auto
                'travelDuration' auto
                'travelNumberOfPeople' auto
                / 1fr;
        }
    }

    .grid-pet {
        @apply grid gap-x-6 gap-y-8;

        grid-template:
            'animalType     animalType' auto
            'animalBreed    animalAge' auto
            / 1fr 1fr;
    }

    @container (max-width: 280px) {
        .grid-pet {
            grid-template:
                'animalType' auto
                'animalBreed' auto
                'animalAge' auto
                / 1fr;
        }
    }

    .grid-life {
        @apply grid gap-x-6 gap-y-8;

        grid-template:
            'lifeMainObjective          lifeMainObjective' auto
            'lifeSubscriberAge          lifeEstimatedAmount' auto
            'lifeDesignatedBeneficiary  lifeDesignatedBeneficiary' auto
            / 1fr 1fr;
    }

    @container (max-width: 379px) {
        .grid-life {
            grid-template:
                'lifeMainObjective' auto
                'lifeSubscriberAge' auto
                'lifeEstimatedAmount' auto
                'lifeDesignatedBeneficiary' auto
                / 1fr;
        }
    }

    .grid-borrower {
        @apply grid gap-x-6 gap-y-8;

        grid-template:
            'borrowerLoanAmount                 borrowerLoanDuration' auto
            'borrowerPropertyTypeFinanced       borrowerHasKnownHealthIssues' auto
            'borrowerWantsToDelegateInsurance   borrowerWantsToDelegateInsurance' auto
            / 1fr 1fr;
    }

    @container (max-width: 629px) {
        .grid-borrower {
            @apply grid gap-x-6 gap-y-8;

            grid-template:
                'borrowerLoanAmount                 borrowerLoanDuration' auto
                'borrowerPropertyTypeFinanced       borrowerPropertyTypeFinanced' auto
                'borrowerHasKnownHealthIssues       borrowerHasKnownHealthIssues' auto
                'borrowerWantsToDelegateInsurance   borrowerWantsToDelegateInsurance' auto
                / 1fr 1fr;
        }
    }

    @container (max-width: 250px) {
        .grid-borrower {
            grid-template:
                'borrowerLoanAmount' auto
                'borrowerLoanDuration' auto
                'borrowerPropertyTypeFinanced' auto
                'borrowerHasKnownHealthIssues' auto
                'borrowerWantsToDelegateInsurance' auto
                / 1fr;
        }
    }

    .grid-temporary {
        @apply grid gap-x-6 gap-y-8;

        grid-template:
            'temporaryInsuredItemOrSituation    temporaryInsuredItemOrSituation' auto
            'temporaryDesiredDuration           temporaryDesiredDuration' auto
            'temporarySubscriptionReason        temporaryCoverageStartDate' auto
            / 1fr 1fr;
    }

    @container (max-width: 525px) {
        .grid-temporary {
            grid-template:
                'temporaryInsuredItemOrSituation' auto
                'temporaryDesiredDuration' auto
                'temporarySubscriptionReason' auto
                'temporaryCoverageStartDate' auto
                / 1fr;
        }
    }
</style>
